import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { LayoutHeaderPanelLink } from '#/layouts/Layout/Header/Panel/LayoutHeaderPanel.styled';
import { LayoutHeaderPanelRowContent } from '#/layouts/Layout/Header/Panel/LayoutHeaderPanelRowContent';
import { isAdmin } from '#/utils/userEnvironment';

import type {
  HamburgerMenuExpandableRowType,
  HamburgerMenuLinkRow,
  HamburgerMenuRow as HamburgerMenuRowData,
} from './data/types/HamburgerMenuData';
import { hamburgerMenuConstantsByRowType } from './hamburgerMenuConstants';
import { HamburgerMenuExpandableRow } from './HamburgerMenuExpandableRow';

type Props = {
  row: HamburgerMenuRowData;
  // 'Hamburger Menu' for top-level rows, 'Hamburger Menu > <Section>' inside an expandable
  trackingContext: string;
  expandedRowTypes: ReadonlySet<HamburgerMenuExpandableRowType>;
  onToggleExpanded: (type: HamburgerMenuExpandableRowType) => void;
};

function getLinkRowHref(row: HamburgerMenuLinkRow, user: ApiUser | null) {
  if (!user) return row.loggedOutHref ?? row.href;
  if (isAdmin(user)) return row.adminUserHref ?? row.href;

  return row.href;
}

export function HamburgerMenuRow({
  row,
  trackingContext,
  expandedRowTypes,
  onToggleExpanded,
}: Props) {
  const { user } = useUser();
  const { icon, qaId, trackingName } =
    hamburgerMenuConstantsByRowType[row.type];

  if ('href' in row) {
    const href = getLinkRowHref(row, user);

    return (
      <li>
        <LayoutHeaderPanelLink
          type="link"
          href={href}
          anchorType={row.fullPageReload ? 'native' : 'router'}
          qaId={qaId}
          clickActions={{
            closeRichDetailsFromWithin: true,
            track: {
              name: trackingName,
              properties: { context: trackingContext },
            },
          }}
        >
          <LayoutHeaderPanelRowContent icon={icon} title={row.title} />
        </LayoutHeaderPanelLink>
      </li>
    );
  }

  return (
    <HamburgerMenuExpandableRow
      type={row.type}
      title={row.title}
      trackingContext={trackingContext}
      expanded={expandedRowTypes.has(row.type)}
      onToggleExpanded={onToggleExpanded}
    >
      {row.rows.map((childRow) => (
        <HamburgerMenuRow
          key={childRow.type}
          row={childRow}
          trackingContext={`${trackingContext} > ${trackingName}`}
          expandedRowTypes={expandedRowTypes}
          onToggleExpanded={onToggleExpanded}
        />
      ))}
    </HamburgerMenuExpandableRow>
  );
}
